import React, { useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Audit log export component for CSV download functionality
 */
const AuditLogExport = ({ filters, disabled }) => {
  const { execute, loading } = useApi();
  const { showSuccess, showError } = useToast();
  const { theme } = useTheme();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: 'csv',
    includeDetails: true,
    includeMetadata: true,
    maxRecords: 10000
  });

  /**
   * Handle export initiation
   */
  const handleExport = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add current filters with proper parameter mapping
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          // Map frontend filter names to backend parameter names
          const paramMap = {
            'operationType': 'operation_type',
            'resourceType': 'resource_type',
            'userId': 'user_id',
            'adminId': 'admin_id',
            'startDate': 'start_date',
            'endDate': 'end_date'
          };
          const paramName = paramMap[key] || key;
          queryParams.append(paramName, value.trim());
        }
      });
      
      // Add export options
      queryParams.append('format', exportOptions.format);
      queryParams.append('includeDetails', exportOptions.includeDetails);
      queryParams.append('includeMetadata', exportOptions.includeMetadata);
      queryParams.append('limit', exportOptions.maxRecords);

      const response = await execute(
        (api) => api.get(`/api/admin/audit/export?${queryParams.toString()}`),
        {
          showErrorToast: false,
          loadingState: false
        }
      );

      // Handle different response types
      if (response instanceof Response) {
        // If it's a direct file response
        const blob = await response.blob();
        downloadFile(blob, `audit-logs-${new Date().toISOString().split('T')[0]}.${exportOptions.format}`);
      } else if (response.downloadUrl) {
        // If it's a URL to download
        window.open(response.downloadUrl, '_blank');
      } else if (response.data) {
        // If it's CSV data in response
        const blob = new Blob([response.data], { type: 'text/csv' });
        downloadFile(blob, `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      }

      showSuccess('Audit logs exported successfully');
      setIsExportModalOpen(false);
    } catch (error) {
      showError(`Export failed: ${error.message}`);
    }
  }, [execute, filters, exportOptions, showSuccess, showError]);

  /**
   * Download file helper
   */
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /**
   * Handle export option changes
   */
  const handleOptionChange = useCallback((option, value) => {
    setExportOptions(prev => ({ ...prev, [option]: value }));
  }, []);

  return (
    <>
      {/* Export Button */}
      <button
        onClick={() => setIsExportModalOpen(true)}
        disabled={disabled || loading}
        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
      >
        {loading ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Exporting...</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <span>📥</span>
            <span>Export</span>
          </div>
        )}
      </button>

      {/* Export Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg border p-6 w-full max-w-md mx-4 ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Export Audit Logs
              </h3>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Format Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Export Format
                </label>
                <select
                  value={exportOptions.format}
                  onChange={(e) => handleOptionChange('format', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="csv">CSV (Comma Separated)</option>
                  <option value="json">JSON</option>
                  <option value="xlsx">Excel (XLSX)</option>
                </select>
              </div>

              {/* Max Records */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Maximum Records
                </label>
                <select
                  value={exportOptions.maxRecords}
                  onChange={(e) => handleOptionChange('maxRecords', parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value={1000}>1,000 records</option>
                  <option value={5000}>5,000 records</option>
                  <option value={10000}>10,000 records</option>
                  <option value={25000}>25,000 records</option>
                  <option value={50000}>50,000 records</option>
                </select>
              </div>

              {/* Include Options */}
              <div className="space-y-3">
                <label className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Include in Export
                </label>
                
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeDetails}
                      onChange={(e) => handleOptionChange('includeDetails', e.target.checked)}
                      className={`rounded text-teal-600 focus:ring-teal-500 ${
                        theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 focus:ring-offset-gray-800'
                          : 'border-gray-300 bg-white focus:ring-offset-white'
                      }`}
                    />
                    <span className={`ml-2 text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Detailed action information
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeMetadata}
                      onChange={(e) => handleOptionChange('includeMetadata', e.target.checked)}
                      className={`rounded text-teal-600 focus:ring-teal-500 ${
                        theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 focus:ring-offset-gray-800'
                          : 'border-gray-300 bg-white focus:ring-offset-white'
                      }`}
                    />
                    <span className={`ml-2 text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      System metadata (IP, User Agent, etc.)
                    </span>
                  </label>
                </div>
              </div>

              {/* Current Filters Summary */}
              {Object.values(filters).some(value => value && value.trim()) && (
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                }`}>
                  <h4 className={`text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Current Filters Applied:
                  </h4>
                  <div className="space-y-1">
                    {Object.entries(filters).map(([key, value]) => {
                      if (!value || !value.trim()) return null;
                      
                      const displayKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                      
                      return (
                        <div key={key} className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          <span className="font-medium">{displayKey}:</span> {value}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsExportModalOpen(false)}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              
              <button
                onClick={handleExport}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Exporting...</span>
                  </div>
                ) : (
                  'Export Logs'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditLogExport;
